import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { accountGeneration, accountStorage, scopeToAccount } from '../lib/accountStorage';
import { calculateLevelBasedPoints, POINTS } from '../utils/levelCalculation';
import { focusColumns, isMissingColumn, warnFocusColumnsMissing, type SessionFocusCapture } from '../lib/sessionFocusCapture';
import type { HabitSession, ActiveHabitSession, HabitSessionType, Habit } from '../types';

interface HabitSessionState {
  activeSession: ActiveHabitSession | null;
  sessions: HabitSession[];
  isLoading: boolean;
  error: string | null;
  userLevel: number;

  startSession: (habit: Habit, sessionType: HabitSessionType, timerDurationSeconds?: number) => Promise<string | null>;
  pauseSession: () => void;
  resumeSession: () => void;
  stopSession: (notes?: string) => Promise<void>;
  /**
   * Attach the end-of-session attention capture to an already-stopped session.
   *
   * ⚠️ Deliberately NOT a parameter of `stopSession`: that computes duration as
   * `now - startedAt`, so prompting for a rating first would silently bill the
   * user's thinking time to the session. Stop first (duration honest), rate after.
   */
  rateSession: (sessionId: string, focus: SessionFocusCapture) => Promise<void>;
  cancelSession: () => Promise<void>;
  updateElapsedTime: () => void;
  calculateSessionXP: (durationSeconds: number) => number;
  fetchSessions: (habitId?: string) => Promise<void>;
  getHabitSessions: (habitId: string) => HabitSession[];
  setUserLevel: (level: number) => void;
  recoverActiveSession: () => Promise<void>;
}

export const useHabitSessionStore = create<HabitSessionState>()(
  persist(
    (set, get) => ({
      activeSession: null,
      sessions: [],
      isLoading: false,
      error: null,
      userLevel: 1,

      setUserLevel: (level) => set({ userLevel: level }),

      calculateSessionXP: (durationSeconds: number) => {
        const state = get();

        if (durationSeconds < 60) {
          return calculateLevelBasedPoints(POINTS.HABIT, state.userLevel);
        }

        const minutes = Math.floor(durationSeconds / 60);
        const timeBasedXP = minutes * 5;
        const baseHabitXP = calculateLevelBasedPoints(POINTS.HABIT, state.userLevel);

        return timeBasedXP + baseHabitXP;
      },

      /* ⚠️⚠️ PER ACCOUNT (lib/accountStorage): the running session is saved on this device —
         the cloud row cannot resume it — and it was one key for everyone, so the next
         account to sign in got the last one's timer. Every answer below that lands after
         a change of account is dropped. */
      startSession: async (habit: Habit, sessionType: HabitSessionType, timerDurationSeconds?: number) => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (at !== accountGeneration()) return null;
          if (!user) throw new Error('Not authenticated');

          const startedAt = new Date().toISOString();

          const { data, error } = await supabase
            .from('habit_sessions')
            .insert({
              user_id: user.id,
              habit_id: habit.id,
              session_type: sessionType,
              duration_seconds: 0,
              xp_earned: 0,
              started_at: startedAt,
              status: 'active',
              paused_duration_seconds: 0
            })
            .select()
            .single();

          if (at !== accountGeneration()) return null;
          if (error) throw error;

          const activeSession: ActiveHabitSession = {
            sessionId: data.id,
            habitId: habit.id,
            habitName: habit.name,
            habitIcon: habit.icon,
            habitCategory: habit.category,
            sessionType,
            startedAt,
            elapsedSeconds: 0,
            pausedDurationSeconds: 0,
            timerDurationSeconds
          };

          set({ activeSession, error: null });
          return data.id;
        } catch (error: any) {
          set({ error: error.message });
          return null;
        }
      },

      pauseSession: () => {
        const state = get();
        if (!state.activeSession) return;

        set({
          activeSession: {
            ...state.activeSession,
            pausedAt: new Date().toISOString()
          }
        });
      },

      resumeSession: () => {
        const state = get();
        if (!state.activeSession || !state.activeSession.pausedAt) return;

        const pausedAt = new Date(state.activeSession.pausedAt);
        const now = new Date();
        const pauseDuration = Math.floor((now.getTime() - pausedAt.getTime()) / 1000);

        set({
          activeSession: {
            ...state.activeSession,
            pausedAt: undefined,
            pausedDurationSeconds: state.activeSession.pausedDurationSeconds + pauseDuration
          }
        });
      },

      rateSession: async (sessionId: string, focus: SessionFocusCapture) => {
        try {
          const { error } = await supabase
            .from('habit_sessions')
            .update(focusColumns(focus))
            .eq('id', sessionId);

          // ⚠️ Before 0212 is run, PostgREST rejects this with 42703. The session
          // itself already stopped and kept its XP, so the rating is simply
          // dropped with a warning rather than surfaced as a failure — and it
          // stops being dropped the moment the migration lands.
          if (error) {
            if (isMissingColumn(error)) {
              warnFocusColumnsMissing();
              return;
            }
            throw error;
          }

          await get().fetchSessions();
        } catch (error: any) {
          set({ error: error.message });
        }
      },

      stopSession: async (notes?: string) => {
        const state = get();
        if (!state.activeSession) return;
        const at = accountGeneration();

        try {
          const completedAt = new Date().toISOString();
          const startedAt = new Date(state.activeSession.startedAt);
          const totalElapsed = Math.floor((new Date(completedAt).getTime() - startedAt.getTime()) / 1000);
          const actualDuration = totalElapsed - state.activeSession.pausedDurationSeconds;

          const xpEarned = state.calculateSessionXP(actualDuration);

          const { error } = await supabase
            .from('habit_sessions')
            .update({
              duration_seconds: actualDuration,
              xp_earned: xpEarned,
              completed_at: completedAt,
              status: 'completed',
              paused_duration_seconds: state.activeSession.pausedDurationSeconds,
              session_notes: notes
            })
            .eq('id', state.activeSession.sessionId);

          if (at !== accountGeneration()) return;
          if (error) throw error;

          set({ activeSession: null, error: null });

          await get().fetchSessions();
        } catch (error: any) {
          set({ error: error.message });
        }
      },

      cancelSession: async () => {
        const state = get();
        if (!state.activeSession) return;
        const at = accountGeneration();

        try {
          const { error } = await supabase
            .from('habit_sessions')
            .update({
              status: 'cancelled'
            })
            .eq('id', state.activeSession.sessionId);

          if (at !== accountGeneration()) return;
          if (error) throw error;

          set({ activeSession: null, error: null });
        } catch (error: any) {
          set({ error: error.message });
        }
      },

      updateElapsedTime: () => {
        const state = get();
        if (!state.activeSession || state.activeSession.pausedAt) return;

        const startedAt = new Date(state.activeSession.startedAt);
        const now = new Date();
        const totalElapsed = Math.floor((now.getTime() - startedAt.getTime()) / 1000);
        const actualElapsed = totalElapsed - state.activeSession.pausedDurationSeconds;

        set({
          activeSession: {
            ...state.activeSession,
            elapsedSeconds: actualElapsed
          }
        });
      },

      fetchSessions: async (habitId?: string) => {
        set({ isLoading: true, error: null });
        const at = accountGeneration();

        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) throw new Error('Not authenticated');

          let query = supabase
            .from('habit_sessions')
            .select('*')
            .eq('user_id', user.id)
            .eq('status', 'completed')
            .order('completed_at', { ascending: false });

          if (habitId) {
            query = query.eq('habit_id', habitId);
          }

          const { data, error } = await query;

          if (at !== accountGeneration()) return;
          if (error) throw error;

          const sessions: HabitSession[] = (data || []).map((row) => ({
            id: row.id,
            userId: row.user_id,
            habitId: row.habit_id,
            sessionType: row.session_type,
            durationSeconds: row.duration_seconds,
            xpEarned: row.xp_earned,
            startedAt: row.started_at,
            completedAt: row.completed_at,
            status: row.status,
            pausedDurationSeconds: row.paused_duration_seconds,
            sessionNotes: row.session_notes,
            // `?? null` (not `undefined`) so a pre-0212 row reads as an explicit
            // "never rated" rather than a missing key — Focus treats both as not a
            // reading, but this keeps the shape honest either way.
            attention: row.attention ?? null,
            distractions: row.distractions ?? null,
            createdAt: row.created_at,
            updatedAt: row.updated_at
          }));

          set({ sessions, isLoading: false });
        } catch (error: any) {
          set({ error: error.message, isLoading: false });
        }
      },

      getHabitSessions: (habitId: string) => {
        return get().sessions.filter(s => s.habitId === habitId);
      },

      recoverActiveSession: async () => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const { data, error } = await supabase
            .from('habit_sessions')
            .select('*')
            .eq('user_id', user.id)
            .in('status', ['active', 'paused'])
            .order('started_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (error || !data) return;

          const startedAt = new Date(data.started_at);
          const now = new Date();
          const hoursSinceStart = (now.getTime() - startedAt.getTime()) / (1000 * 60 * 60);

          if (hoursSinceStart > 24) {
            await supabase
              .from('habit_sessions')
              .update({ status: 'cancelled' })
              .eq('id', data.id);
            return;
          }

        } catch (error: any) {
          console.error('Error recovering active session:', error);
        }
      }
    }),
    {
      name: 'habit-session-storage',
      version: 1,
      partialize: (state) => ({
        activeSession: state.activeSession,
        userLevel: state.userLevel
      }),
      storage: accountStorage('claim'),
    }
  )
);

scopeToAccount(useHabitSessionStore);
